import React, { useState, useCallback, useEffect, useMemo } from 'react';
import PropTypes from 'prop-types';
import moment from 'moment';

import Icon from '../../../../Icon';
import Button from '../../../../Button';
import { formatDate } from '../../../utils';
import { INVALID_DATE } from '../../../constants';
import { BREAKPOINT, COLORS } from '../../../../../constants/styles';
import { DatePickerRange } from '../../../../common/DatePickerRange';

import styles from './index.styl';

const HealthRecordSearch = ({
  pending,
  endDate,
  onClear,
  onSearch,
  startDate,
  setEndDate,
  setStartDate,
  formatMessage,
}) => {
  const [isNewDate, setIsNewDate] = useState(false);
  const [direction, setDirection] = useState('horizontal');
  const [openDatePickerRange, setOpenDatePickerRange] = useState(false);

  const hasData = startDate && endDate;
  const safeToTitle = formatMessage({ id: 'common.to' });
  const safeFromTitle = formatMessage({ id: 'common.from' });

  const formattedDate = useCallback(() => {
    const fromDate = formatDate(startDate, 'MMM D, Y');
    const toDate = formatDate(endDate, 'MMM D, Y');

    const safeStartDate = fromDate === INVALID_DATE ? safeFromTitle : fromDate;
    const safeEndDate = toDate === INVALID_DATE ? safeToTitle : toDate;
    const rangeOneDay = safeStartDate === safeEndDate;
    return rangeOneDay ? safeStartDate : `${safeStartDate} - ${safeEndDate}`;
  }, [startDate, endDate]);

  const { disableClearButton, disableSearchButton } = useMemo(
    () => ({
      disableClearButton: !hasData || pending,
      disableSearchButton:
        (Boolean(hasData) && !isNewDate) || !hasData || pending,
    }),
    [hasData, isNewDate, pending],
  );

  const defineViewDate = moment()
    .subtract(1, 'month')
    .toDate();

  const onSearchHandler = () => {
    setOpenDatePickerRange(false);
    setIsNewDate(false);
    onSearch();
  };

  useEffect(() => {
    setDirection(window.innerWidth <= BREAKPOINT ? 'vertical' : 'horizontal');
  }, []);

  useEffect(() => {
    setIsNewDate(true);
  }, [startDate, endDate]);

  return (
    <div className={styles.root}>
      <div className={styles.title}>
        {formatMessage({ id: 'patient.portal.healthRecord.dateRange' })}
      </div>
      <div className={styles.searchWrapper}>
        <div
          className={styles.searchBox}
          onClick={() => setOpenDatePickerRange(!openDatePickerRange)}
        >
          <div className={styles.date}>{formattedDate()}</div>
          <Icon
            size={16}
            name="icon-date"
            style={{ fill: COLORS.stormyBlue }}
          />
        </div>
        <Button
          icon
          onClick={onClear}
          className={styles.clearDate}
          disabled={disableClearButton}
          aria-label="close"
        >
          <Icon size={16} name="close" style={{ fill: COLORS.stormyBlue }} />
        </Button>
        <Button
          raised
          color="primary"
          onClick={onSearchHandler}
          disabled={disableSearchButton}
          className={styles.searchButton}
        >
          {formatMessage({ id: 'patient.portal.healthRecord.search' })}
        </Button>
      </div>
      {openDatePickerRange && (
        <DatePickerRange
          months={2}
          direction={direction}
          endDateRange={endDate}
          rangeColors={['#5476a4']}
          shownDate={defineViewDate}
          startDateRange={startDate}
          disabledMaxDate={new Date()}
          setEndDateRange={setEndDate}
          setStartDateRange={setStartDate}
          className={styles.wrapperCalendar}
        />
      )}
    </div>
  );
};

HealthRecordSearch.propTypes = {
  onSearch: PropTypes.func,
  pending: PropTypes.bool,
  onClear: PropTypes.func,
  setEndDate: PropTypes.func,
  setStartDate: PropTypes.func,
  formatMessage: PropTypes.func,
  endDate: PropTypes.oneOfType([PropTypes.instanceOf(Date), PropTypes.string]),
  startDate: PropTypes.oneOfType([
    PropTypes.instanceOf(Date),
    PropTypes.string,
  ]),
};

export default HealthRecordSearch;



// WEBPACK FOOTER //
// ./src/components/PatientHealthboard/PatientPortal/DataList/HealthRecordSearch/index.jsx